import { parseReturnPath } from '@traceio-org/platform-contracts';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const SETTINGS_SECTION = '(?:overview|members|invitations|modules|domains|help-desk|audit|billing)';
/** A ticket number as the contract writes it (TICKET_NUMBER_PATTERN): NWF-142, T-10421. */
const TICKET_NUMBER = '[A-Z]{1,5}-[1-9][0-9]{0,11}';

/**
 * Hub routes a sign-in may come back to (carried as appState.hubPath through the identity provider): invitation
 * pages, the start page, the tickets pages, and the organization settings (the list, one organization, one of its known sections; no query, so no
 * search or cursor rides along, except the billing page's from=payment, which only asks to read the card fresh). Every other destination goes through navigation/decide instead. The path first
 * passes parseReturnPath (same origin, decoded until it stops changing, dot segments resolved), and only then
 * one of these exact shapes, so an encoded detour to an auth route can never match.
 */
const HUB_RETURN_ROUTES: readonly RegExp[] = [
  /^\/invitations\/[A-Za-z0-9._~-]{1,256}$/,
  new RegExp(`^/settings(?:/${UUID}(?:/${SETTINGS_SECTION})?)?$`),
  // The help desk's new ticket and ticket pages.
  new RegExp(`^/settings/${UUID}/help-desk/(?:new|${TICKET_NUMBER})$`),
  // My tickets, a new ticket, and one ticket (the link every ticket email carries).
  new RegExp(`^/tickets(?:/${UUID}/(?:new|${TICKET_NUMBER}))?$`),
  // The billing page as the billing provider's payment page returns to it (from=payment: read the card fresh).
  new RegExp(`^/settings/${UUID}/billing\\?from=payment$`),
  // Start your trial (self-serve sign-up, spec 2026-10-18 piece 5): the page, with the pricing page's setup code (commas
  // raw or encoded), and the page Stripe Checkout returns to for one sign-up.
  /^\/start(?:\?setup=(?:[a-z0-9.,]|%2[Cc]){1,250})?$/,
  new RegExp(`^/start\\?signup=${UUID}&checkout=(?:done|canceled)$`),
];

export function safeHubPath(value: unknown): string | undefined {
  const result = parseReturnPath(value);
  if (!result.ok) return undefined;
  return HUB_RETURN_ROUTES.some((pattern) => pattern.test(result.path)) ? result.path : undefined;
}
